import { Loader2, Pause, Play } from "lucide-react";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/v2/components/ui/tooltip";
import { TooltipButton } from "@/v2/components/ui/tooltip-button";
import {
	useSmartTimer,
	useSmartTimerDisplaySeconds,
} from "@/v2/hooks/useSmartTimer";
import { cn } from "@/v2/lib/utils";

function formatTime(totalSeconds: number): string {
	const h = Math.floor(totalSeconds / 3600);
	const m = Math.floor((totalSeconds % 3600) / 60);
	const s = totalSeconds % 60;
	const mm = String(m).padStart(2, "0");
	const ss = String(s).padStart(2, "0");
	return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * Global smart-timer control. Idle → a Play that starts a timer on a new empty
 * group. Running → a pill showing the group's live kept time (start + elapsed,
 * ticking) with a pause. No empty row is inserted; the group appears once work is
 * captured (or, on pause, once the remainder becomes an "Other tasks" entry).
 */
export function ToolbarTimerButton() {
	const {
		activeEntryGroupId,
		pausingEntryGroupId,
		isStartingNew,
		isBusy,
		isSavingEntries,
		startNew,
		pause,
	} = useSmartTimer();
	const displaySeconds = useSmartTimerDisplaySeconds();

	const isPausing = pausingEntryGroupId !== null;
	// Stay in the "running" visual through an in-flight pause so it doesn't flicker
	// back to Play mid-request.
	const running = activeEntryGroupId !== null || isPausing;
	const showSpinner = isStartingNew || isPausing;

	if (!running) {
		return (
			<TooltipButton
				variant="outline"
				size="icon"
				aria-label="Start a timer on a new entry group"
				tooltip="Start a timer on a new entry group"
				onClick={() => {
					if (!isBusy) startNew();
				}}
				disabled={(isBusy || isSavingEntries) && !showSpinner}
				aria-busy={isBusy}
			>
				{isStartingNew ? (
					<Loader2 className="animate-spin" />
				) : (
					<Play className="fill-current" />
				)}
			</TooltipButton>
		);
	}

	return (
		<div className="flex h-10 items-center gap-1.5 rounded-md border border-ring bg-background pl-3 pr-1 text-ring">
			<div className="flex items-center gap-1.5">
				<div
					className={cn(
						"size-2 rounded-full bg-ring",
						!showSpinner && "animate-pulse",
					)}
				/>
				<span className="tabular-nums text-sm font-semibold text-foreground">
					{formatTime(displaySeconds)}
				</span>
			</div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type="button"
							onClick={() => {
								if (!isBusy) pause();
							}}
							disabled={isBusy && !showSpinner}
							aria-busy={isBusy}
							aria-label="Pause"
							aria-pressed
							className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground active:bg-accent/80"
						>
							{showSpinner ? (
								<Loader2 className="size-4 animate-spin" />
							) : (
								<Pause className="size-4" />
							)}
						</button>
					</TooltipTrigger>
					<TooltipContent side="bottom">Pause</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		</div>
	);
}
